import React from 'react';
import PropTypes from 'prop-types';
import CMSLayout from './layout/CMSLayout/CMSLayout';
import { useSelector } from 'react-redux';
import { isNull, isObject, isUndefined } from 'lodash';
import { useMemo } from 'react';
import { HttpError } from './common/HttpError';

import {
  ROLE_ING_PROJECTS_ADMIN,
  ROLE_ING_PROJECTS_GUARANTOR,
  ROLE_ING_PROJECT_STUDENT,
  ROLE_STUDY_PROGRAM_GUARANTEE,
  ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR,
} from '../utils/constants/roles.js';

const getRoleForPermission = (permission) => {
  switch (permission) {
    case 'ing-projects-admin':
      return ROLE_ING_PROJECTS_ADMIN;
    case 'ing-projects-project-guarantee':
      return ROLE_ING_PROJECTS_GUARANTOR;
    case 'ing-projects-study-program-guarantee':
      return ROLE_STUDY_PROGRAM_GUARANTEE;
    case 'ing-projects-assignments':
      return ROLE_ING_PROJECTS_ASSIGNMENT_COORDINATOR;
    case 'ing-projects-student':
      return ROLE_ING_PROJECT_STUDENT;
    default:
      return null;
  }
};

const CMSWrapper = ({ permissions, permissionsAny, children }) => {
  const user = useSelector((state) => state.auth.user);
  const errorStatusCode = useSelector((state) => state.core.errorStatus);
  const errorMessage = useSelector((state) => state.core.apiErrorMessage);
  const hasUserPermissions = useMemo(() => {
    if (isUndefined(user)) {
      return false;
    }

    if (isUndefined(permissions) && isUndefined(permissionsAny)) {
      return true;
    }

    const permissionMap = isObject(user?.permissions) ? user.permissions : {};
    const userRoles = Array.isArray(user?.roles) ? user.roles.map((role) => role?.name).filter(Boolean) : [];
    const hasPermission = (permission) => {
      if (!permission) return true;
      if (permission in permissionMap) return true;

      const mappedRoleName = getRoleForPermission(permission);
      return mappedRoleName ? userRoles.includes(mappedRoleName) : false;
    };

    const allPermissions = Array.isArray(permissions)
      ? permissions
      : permissions ? [permissions] : [];
    const anyPermissions = Array.isArray(permissionsAny)
      ? permissionsAny
      : permissionsAny ? [permissionsAny] : [];

    const hasAllRequired = allPermissions.every(hasPermission);
    const hasAnyPermission = anyPermissions.length === 0 || anyPermissions.some(hasPermission);

    return hasAllRequired && hasAnyPermission;
  }, [user, permissions, permissionsAny]);

  if (isUndefined(user) || isNull(user) || (!hasUserPermissions && (permissions || permissionsAny))) {
    return (
      <CMSLayout>
        <HttpError
          showTitle={false}
          showShadow={false}
          inCMS={true}
          statusCode={403}
        />
      </CMSLayout>
    );
  }

  if (errorStatusCode === 404 || errorStatusCode === 403) {
    return (
      <CMSLayout>
        <HttpError
          showTitle={false}
          showShadow={false}
          statusCode={errorStatusCode}
          inCMS={true}
          message={errorMessage}
        />
      </CMSLayout>
    );
  }

  // user has permission and is logged in, show content
  return <CMSLayout>{children}</CMSLayout>;
};

CMSWrapper.propTypes = {
  children: PropTypes.node,
  permissions: PropTypes.arrayOf(PropTypes.string),
  permissionsAny: PropTypes.arrayOf(PropTypes.string),
};

export default CMSWrapper;
